import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import AppBar from "../../components/atoms/appbar";
import Button from "../../components/atoms/button";
import PageCount from "../../components/atoms/count/PageCount";
import Tabs, { useTabs } from "../../components/atoms/tab";
import { withTabContnet } from "../../components/atoms/tab/withTabContent";
import CompleteScreen from "../../components/layout/reservation/CompleteScreen";
import PaymentScreen from "../../components/layout/reservation/PaymentScreen";
import MemberScreen from "../../components/layout/reservation/MemberScreen";
import CourseScreen from "../../components/layout/reservation/CourseScreen";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentButton from "../../components/atoms/button/PaymentButton";
import type { ResveListItem } from "../../@apis/dto/response/resve.response";
import FailScreen from "../../components/layout/reservation/FailScreen";
import { useModalStore } from "../../lib/store/useModalStore";

// 폼 타입 (필요한 최소값만)
type ReservationForm = {
  selectedCourse?: string;
  allAgreed?: boolean;
  memberCount: number;
  resveDetail: ResveListItem;
  alreadyReservedByMe?: boolean;
};

export default function ReservationAdd() {
  const { openModal } = useModalStore();

  const location = useLocation();
  const { golfParkCode, date } = location.state || {};

  const navigate = useNavigate();

  const { currentTab, next, prev, move } = useTabs(1);

  const CourseScreenTab = React.useMemo(() => withTabContnet(CourseScreen, { golfParkCode, date }), [golfParkCode, date]);
  const MemberScreenTab = React.useMemo(() => withTabContnet(MemberScreen, { golfParkCode, date, prev }), [golfParkCode, date, prev]);
  const PaymentScreenTab = React.useMemo(() => withTabContnet(PaymentScreen, { golfParkCode, date }), [golfParkCode, date]);
  const CompleteScreenTab = React.useMemo(() => withTabContnet(CompleteScreen, {}), []);
  const FailScreenTab = React.useMemo(() => withTabContnet(FailScreen, {  }), []);


  const methods = useForm<ReservationForm>({
    defaultValues: { selectedCourse: "", allAgreed: false, memberCount: 1, alreadyReservedByMe: false },
  });

  const selectedCourse = methods.watch("selectedCourse");
  const allAgreed = methods.watch("allAgreed");

  // 버튼 활성화 로직
  const disabledButton = () => {
    if (currentTab === 1) {
      return !selectedCourse;   
    }
    if (currentTab === 3) {
      return !allAgreed;
    }
    return false;
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    // 탭 전환 직후 레이아웃이 바뀌니 rAF 안에서 0으로
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    });
  }, [currentTab]);

  React.useEffect(() => {
    if (currentTab === 1) {
      methods.setValue("selectedCourse", "");  // 탭1로 돌아올 때마다 선택 초기화
      methods.setValue("alreadyReservedByMe", false);
    }
  }, [currentTab, methods]);


  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {Number(currentTab) < 4 && <AppBar title="예약하기" backButton />}

      <section className="flex-1 flex flex-col min-h-0">
        {Number(currentTab) < 4 && (
          <PageCount
            className="px-[16px] mb-[25px] mt-[1px]"
            totalCount={3}
            currentPage={currentTab as number}
          />
        )}

        <div
          ref={scrollRef}
          className={`flex-1 min-h-0 ${
            currentTab === 1 ? "overflow-hidden" : Number(currentTab) < 4 ? "overflow-y-auto pb-[100px]" : ""
          }`}
        >
          <FormProvider {...methods}>
            <Tabs currentTab={currentTab}>
              <CourseScreenTab />
              <MemberScreenTab />
              <PaymentScreenTab />
              <CompleteScreenTab />
              <FailScreenTab />
            </Tabs>
          </FormProvider>
        </div>

        {currentTab !== 4 && (
          <div className="sticky bottom-0 left-0 right-0 z-50">
            <div className="w-full max-w-[440px] mx-auto p-[15px] bg-white flex gap-[10px]">
              {currentTab !== 1 && Number(currentTab) < 4 && (
                <Button
                  className="w-full h-[50px]"
                  variant={"white"}
                  onClick={prev}
                  label="이전"
                />
              )}

              {currentTab === 3 ? (
                <PaymentButton
                  disabled={disabledButton()}
                  resveNum={methods.watch("memberCount")}
                  resveSeq={methods.watch("resveDetail.resveSeq")}
                  onSuccess={() => navigate("/setting/reservation-history")}
                  onFailed={() => move(5)}
                />
              ) : (
                <>
                  {Number(currentTab) < 4 && (
                    <Button
                      className="w-full h-[50px]"
                      onClick={() => {
                        if (currentTab === 1) {
                          const alreadyReservedByMe = methods.getValues("alreadyReservedByMe");

                          if (alreadyReservedByMe) {
                            openModal("ALERT_DIALOG", { title: "에러", content: "이미 예약한 타임입니다. \n다른 타임을 선택해주세요." });
                            return; 
                          }
                        }

                        if (!disabledButton()) next();
                      }}
                      variant={disabledButton() ? "disabled" : "black"}
                      label={"다음"}
                    />
                  )}
                </>
              )}

            </div>
          </div>
        )}
      </section>
    </div>
  );
}
